import type { ReactNode } from "react";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { EvalRunDetailChip } from "./EvalRunDetailElements";
import {
  criterionValue,
  successCriteriaRows,
  type RunExecution,
  type SuccessCriterionRow,
} from "./evalRunScenarioModel";
import type { EvalImageMetadataState } from "./useEvalImageMetadata";
import styles from "./EvalRunDetailView.module.css";

export function EvalSuccessCriteria({
  execution,
  metadataState,
  onRetry,
}: {
  execution: RunExecution;
  metadataState: EvalImageMetadataState;
  onRetry: () => void;
}) {
  const { tag, task: taskName } = execution;
  const task =
    metadataState.status === "success"
      ? metadataState.data?.tasks.find((candidate) => candidate.name === taskName)
      : undefined;
  const criteria = successCriteriaRows(task);

  // 칩 줄을 대신하는 안내다. 칩 줄(flex) 안에 넣으면 안내가 글자 폭으로 줄어든다. 그래서 칩 줄 밖에 둔다.
  let criteriaState: ReactNode = null;
  if (tag === null) {
    criteriaState = (
      <EmptyState size="sm">Image tag가 없어 성공 기준을 확인할 수 없습니다.</EmptyState>
    );
  } else if (metadataState.status === "idle" || metadataState.status === "loading") {
    criteriaState = <LoadingState size="sm">성공 기준을 불러오는 중입니다…</LoadingState>;
  } else if (metadataState.status === "error") {
    criteriaState = (
      <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: onRetry }}>
        성공 기준을 불러오지 못했습니다: {metadataState.message.slice(0, 180)}
      </EmptyState>
    );
  } else if (metadataState.data === null) {
    criteriaState = (
      <EmptyState size="sm">이 image는 성공 기준 metadata를 제공하지 않습니다.</EmptyState>
    );
  } else if (task === undefined) {
    criteriaState = (
      <EmptyState size="sm">
        Image metadata 에 Task &quot;{taskName ?? "-"}&quot; 가 없습니다.
      </EmptyState>
    );
  } else if (criteria.length === 0) {
    criteriaState = (
      <EmptyState size="sm">이 image 는 성공 기준 metadata 를 제공하지 않습니다.</EmptyState>
    );
  }

  return (
    <div className={styles.chipGroup}>
      <span className={styles.muted}>성공 기준</span>
      {criteriaState ?? <EvalSuccessCriteriaChips criteria={criteria} />}
    </div>
  );
}

export function EvalSuccessCriteriaChips({
  criteria,
}: {
  criteria: readonly SuccessCriterionRow[];
}) {
  return (
    <div className={styles.chips}>
      {criteria.map((criterion) => (
        <EvalRunDetailChip
          key={criterion.name}
          label={criterion.name}
          value={criterionValue(criterion)}
          description={criterion.description}
        />
      ))}
    </div>
  );
}
